import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { markFeedSeen, readFeedSeen } from '../../shared/utils/feed-seen';

const useClientLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;
// Kartın en az yarısı (ekrandan uzun kartta ekranın yarısı) 1 saniye görünürse görüldü sayılır.
const SEEN_DELAY_MS = 1000;
const visibleEnough = (entry: IntersectionObserverEntry) =>
  entry.isIntersecting &&
  (entry.intersectionRatio >= 0.5 ||
    entry.intersectionRect.height >= (entry.rootBounds?.height || window.innerHeight) / 2);

/**
 * S3 görülen kartlar (yalnız ana sayfa, girişli izleyici). Dönen küme sayfa açılışındaki kayıttır;
 * okuma sırasında görülen kart bu açılışta yerinden oynamaz, sıralamaya bir sonraki açılışta girer.
 */
export default function useFeedSeen(
  root: React.RefObject<HTMLElement>,
  viewerId: string | null | undefined,
  // Yeni kartlar çizilince gözlenecek kartlar yeniden taranır.
  key: unknown,
): Set<string> {
  const [seen, setSeen] = useState<Set<string>>(() => new Set());
  const observer = useRef<IntersectionObserver | null>(null);

  useClientLayoutEffect(() => {
    setSeen(new Set(viewerId ? Object.keys(readFeedSeen(viewerId)) : []));
  }, [viewerId]);

  useEffect(() => {
    if (!viewerId || typeof IntersectionObserver === 'undefined') return undefined;
    const timers = new Map<Element, number>();
    const current = new IntersectionObserver(
      entries =>
        entries.forEach(entry => {
          const card = entry.target as HTMLElement;
          if (!visibleEnough(entry)) {
            window.clearTimeout(timers.get(card));
            timers.delete(card);
            return;
          }
          if (timers.has(card)) return;
          timers.set(
            card,
            window.setTimeout(() => {
              markFeedSeen(viewerId, (card.dataset.feedEvents || '').split(' ').filter(Boolean));
              current.unobserve(card);
              timers.delete(card);
            }, SEEN_DELAY_MS),
          );
        }),
      { threshold: [0, 0.25, 0.5, 0.75, 1] },
    );
    observer.current = current;
    return () => {
      timers.forEach(timer => window.clearTimeout(timer));
      current.disconnect();
      observer.current = null;
    };
  }, [viewerId]);

  useEffect(() => {
    root.current
      ?.querySelectorAll('[data-feed-events]')
      .forEach(card => observer.current?.observe(card));
  }, [viewerId, key]);

  return seen;
}
